import { useState, useMemo, useEffect, useContext, useCallback, useRef } from "react";
import AppBackService from "../services/appback";
import { RootContext } from "../context/root-provider";
import { Link, Navigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { Skeleton } from "@mui/material";
import Taps from "../components/Taps";
import CreateTapModal from "../components/CreateTapModal";
import DocShareModal from "../components/DocShareModal";
import SourceAudioPanel from "../components/SourceAudioPanel";
import { audioDeleteFailureMessage } from "../components/sourceAudio";
import { canEditDoc, sharedByLabel } from "../components/docSharing";
import { useSnackbar } from "../components/AlertNotification";
import { usePageHeader } from "../context/PageHeaderContext";
import { useDocumentActions, useTapActions } from "../hooks/hooks";
import ErrorIcon from "@mui/icons-material/Error";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ShareIcon from "@mui/icons-material/Share";
import PeopleIcon from "@mui/icons-material/People";
import PaletteIcon from "@mui/icons-material/Palette";
import RefreshIcon from "@mui/icons-material/Refresh";
import { canRetry } from "../services/docRetry";
import { keptAudio, spokenOf } from "../services/spokenDocs";
import s from "./DocumentPage.module.scss";

// appback sends `created` as epoch millis or an ISO string.
const parseCreated = (doc) => {
  if (doc.created) {
    doc.created = dayjs(isNaN(+doc.created) ? doc.created : +doc.created);
  }
  return doc;
};

// The doc once its kept audio is deleted: the doc read's audio is none.
const withoutAudio = (doc) => {
  const source = doc?.spoken?.source;
  if (!source) return doc;
  return { ...doc, spoken: { ...doc.spoken, source: { ...source, audio: { status: "none" } } } };
};

const DocumentPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [doc, setDoc] = useState(null);
  // Not yours and not shared with you (any more): appback answers with no docs.
  const [unavailable, setUnavailable] = useState(false);
  const [createTapModalOpen, setCreateTapModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [redirect, setRedirect] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const { userInfo, loggingIn } = useContext(RootContext);
  const { docId } = useParams();
  const { showSnackbar, snackbarEl } = useSnackbar();
  const { setHeaderContent } = usePageHeader();

  // document action handlers

  const updateTitle = useCallback(
    (title) => {
      if (!title) {
        showSnackbar("Unable to update title", "error");
      } else {
        setDoc((doc) => ({ ...doc, title }));
        showSnackbar("Document title updated", "success");
      }
    },
    [showSnackbar]
  );

  const resolveDelete = useCallback(
    (docId) => {
      if (!docId) {
        showSnackbar("Unable to delete Document", "error");
      } else {
        setRedirect(true);
      }
    },
    [showSnackbar]
  );

  const { setDeleting, deleteConfirm, setEditingTitle, editTitleDialog } = useDocumentActions(
    updateTitle,
    resolveDelete
  );

  const resolveTapDelete = useCallback(
    (tapId) => {
      if (!tapId) {
        showSnackbar("Unable to delete Tapestry", "error");
      } else {
        setDoc((doc) => {
          const docCopy = { ...doc };
          const tapIndex = docCopy.taps.findIndex((tap) => tap.tapId === tapId);
          if (tapIndex >= 0) {
            docCopy.taps.splice(tapIndex, 1);
          }
          return docCopy;
        });
        showSnackbar("Tapestry deleted", "success");
      }
    },
    [showSnackbar]
  );

  const { setDeletingTap, deleteTapConfirm } = useTapActions(resolveTapDelete);

  const deleteTap = useCallback(
    (tapId, title) => {
      setDeletingTap({ title, tapId });
    },
    [setDeletingTap]
  );

  // initiate document actions

  const editTitle = useCallback(() => {
    setEditingTitle({ title: doc.title, docId });
  }, [doc, docId, setEditingTitle]);

  const deleteDoc = useCallback(() => {
    setDeleting({ title: doc.title, docId });
  }, [doc, docId, setDeleting]);

  const fetchDoc = useCallback(
    async (showLoading = true) => {
      if (userInfo?.username) {
        if (showLoading) {
          setLoading(true);
        }
        // The doc read gives what the doc model says (failure, source and
        // its audio) beside the doc's own record (content, taps, sharing).
        const [data, read] = await Promise.all([
          AppBackService.getUserDoc(userInfo.username, docId),
          AppBackService.getSpokenDoc(userInfo.username, docId),
        ]);
        if (!data || data.error) {
          setError(true);
        } else {
          if (data.docs?.length) {
            data.docs.forEach(parseCreated);
            setDoc({ ...data.docs[0], spoken: spokenOf(read) });
            setUnavailable(false);
          } else {
            setDoc(null);
            setUnavailable(true);
          }
        }
        if (showLoading) {
          setLoading(false);
        }
      }
    },
    [docId, userInfo]
  );

  // The doc the page shows now, for reads that land after a navigation.
  const docIdRef = useRef(docId);
  docIdRef.current = docId;

  // A background refresh after an action: a failed or empty read keeps the doc
  // already on screen rather than flipping the page to its error state, and a
  // read that lands after the page moved to another doc is dropped.
  const refreshDocQuietly = useCallback(async () => {
    if (!userInfo?.username) return;
    const askedFor = docId;
    const [data, read] = await Promise.all([
      AppBackService.getUserDoc(userInfo.username, askedFor),
      AppBackService.getSpokenDoc(userInfo.username, askedFor),
    ]);
    if (docIdRef.current !== askedFor) return;
    const fresh = data && !data.error ? data.docs?.[0] : null;
    const spoken = spokenOf(read);
    if (fresh) setDoc((doc) => ({ ...parseCreated(fresh), spoken: spoken || doc?.spoken || null }));
  }, [docId, userInfo]);

  const retryDoc = useCallback(async () => {
    if (!userInfo?.username || !docId || retrying) return;
    setRetrying(true);
    try {
      const resp = await AppBackService.retryUserDoc(userInfo.username, docId);
      if (resp && !resp.error) {
        showSnackbar("Retry started", "success");
        setDoc((doc) => ({ ...doc, spoken: doc.spoken && { ...doc.spoken, failure: null } }));
        fetchDoc(false);
        return;
      }
      showSnackbar("Unable to retry document", "error");
    } catch (error) {
      showSnackbar("Unable to retry document", "error");
    } finally {
      setRetrying(false);
    }
  }, [docId, fetchDoc, retrying, showSnackbar, userInfo?.username]);

  // 204 (also when there was no audio left): drop the panel, which is then
  // exactly the server's state. A refusal is worded by status (409: retention
  // busy) and the doc re-read quietly, so the panel matches what is there.
  const resolveAudioDelete = useCallback(
    (deletedDocId, resp) => {
      if (deletedDocId) {
        if (docIdRef.current === deletedDocId) setDoc(withoutAudio);
        showSnackbar("Audio deleted", "success");
        return;
      }
      showSnackbar(audioDeleteFailureMessage(resp?.status), resp?.status === 409 ? "warning" : "error");
      refreshDocQuietly();
    },
    [refreshDocQuietly, showSnackbar]
  );

  // Inject header content into navbar
  useEffect(() => {
    if (loading || !doc) return;
    setHeaderContent(
      <div className={s.navHeaderContent}>
        <span
          className={canEditDoc(doc) ? s.navTitle : `${s.navTitle} ${s.navTitleReadOnly}`}
          title={doc.title}
          onClick={canEditDoc(doc) ? editTitle : undefined}>
          {doc.title} {canEditDoc(doc) && <EditIcon className={s.navTitleEdit} />}
        </span>
        <div className={s.navActions}>
          {doc.shared ? (
            <span className={s.sharedTag} title="Shared with you">
              <PeopleIcon /> {sharedByLabel(doc)}
            </span>
          ) : (
            <>
              <button className={s.navBtn} onClick={() => setShareModalOpen(true)}>
                <ShareIcon /> Share
              </button>
              <button className={s.navBtnDanger} onClick={deleteDoc}>
                <DeleteOutlineIcon /> Delete
              </button>
            </>
          )}
          {canEditDoc(doc) && (
            <button className={s.navBtnCreate} onClick={() => setCreateTapModalOpen(true)}>
              <PaletteIcon /> Create Tapestry
            </button>
          )}
        </div>
      </div>
    );
    return () => setHeaderContent(null);
  }, [loading, doc, editTitle, deleteDoc, setHeaderContent]);

  // init, fetch doc
  useEffect(() => {
    if (userInfo?.username && !loggingIn) {
      fetchDoc();
    }
  }, [fetchDoc, loggingIn, userInfo]);

  const docSkeleton = useMemo(
    () => (
      <div className={s.container}>
        <Skeleton variant="rectangular" width="100%" height={62} className={s.skeleton} />
        <Skeleton variant="rectangular" width="100%" height={100} className={s.skeleton} />
        <Skeleton variant="rectangular" width="100%" height={400} className={s.skeleton} />
      </div>
    ),
    []
  );

  if (!docId || redirect) {
    return <Navigate to="/" />;
  }
  if (error || unavailable) {
    return (
      <div className={s.errorContainer}>
        <ErrorIcon />
        <p className={s.errorMessage}>
          {unavailable
            ? "This document isn't available to you. It may have been deleted, or its owner stopped sharing it."
            : "Couldn't load this document. Try again in a moment."}
        </p>
        <Link to="/">Back to your documents</Link>
      </div>
    );
  }
  const displayContent = doc?.content || "";
  const failure = doc?.spoken?.failure;
  const retryable = !doc?.shared && canRetry(failure);
  // The kept audio is the owner's; the doc read signs it for them alone.
  const audio = doc?.shared ? null : keptAudio(doc?.spoken);
  return (
    <div className={s.container}>
      {loading || !doc ? (
        docSkeleton
      ) : (
        <>
          {doc.taps && (
            <div className={s.tapsContainer}>
              <Taps taps={doc.taps} deleteTap={canEditDoc(doc) ? deleteTap : undefined} />
            </div>
          )}
          {audio && (
            <SourceAudioPanel
              docId={docId}
              docTitle={doc.title}
              sourceAudio={audio}
              resolveDelete={resolveAudioDelete}
            />
          )}
          <div className={s.docContent}>
            <ContentCopyIcon
              className={s.copyButton}
              onClick={() => {
                navigator.clipboard.writeText(displayContent);
                showSnackbar("Document copied to clipboard", "success");
              }}
            />
            {retryable && (
              <div className={s.retryActions}>
                <span role="status">
                  This document&apos;s source could not be completed{failure.message ? `: ${failure.message}` : "."}
                </span>
                <button className={s.retryBtn} onClick={retryDoc} disabled={retrying}>
                  <RefreshIcon /> Retry
                </button>
              </div>
            )}
            {displayContent}
          </div>
          {deleteConfirm}
          {editTitleDialog}
          {deleteTapConfirm}
          {snackbarEl}
          <CreateTapModal
            open={createTapModalOpen}
            setOpen={setCreateTapModalOpen}
            doc={doc}
            refreshCallback={fetchDoc}
            showSnackbar={showSnackbar}
          />
          <DocShareModal
            open={shareModalOpen}
            handleClose={() => setShareModalOpen(false)}
            doc={doc}
            showSnackbar={showSnackbar}
          />
        </>
      )}
    </div>
  );
};

export default DocumentPage;
